import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, Clock, Film, Layers, Lock, Play, Plus, Search, Trash2, Upload, X } from "lucide-react";
import { SlideshowsProvider, useSlideshows, type Slideshow } from "./SlideshowsProvider";
import { MediaBrowser } from "../../components/MediaBrowser/MediaBrowser";
import {
  MediaProvider,
  type MediaFile as MediaProviderFile,
  type MediaFolder as MediaProviderFolder,
} from "../media/MediaProvider";
import { Folder as FolderIcon } from "lucide-react";
import { HierarchyPicker } from "../../components/HierarchyPicker/HierarchyPicker";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";
import type { EditableSlideshowMediaItem, MediaOrientation } from "../../../../common/src/types";

type MediaFileInfo = {
  id: string;
  originalFilename: string;
  contentType: string;
  downloadUrl: string;
  sizeBytes: number;
  metadata?: {
    duration?: number;
    width?: number;
    height?: number;
    orientation?: MediaOrientation;
  };
};

type UploadResult = {
  file: MediaFileInfo;
};

type IndyConnection = { connected: false } | { connected: true; email: string | null; clientName: string | null };

type IndyConnectionResponse = {
  connection: IndyConnection;
};

type IndyExternalSlideshow = {
  id: number;
  name: string;
  status: string;
  interval: number;
  attachmentCount: number;
  attachments: { id: number; name: string; fileType: string; order: number }[];
};

type IndySlideshowsResponse = {
  slideshows: IndyExternalSlideshow[];
};

type IndyImportResponse = {
  imported: { externalId: number; slideshow: Slideshow }[];
  failed: { externalId: number; name: string; error: string }[];
};

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899"];

const BANNER_COLORS = {
  orange: "#f97316",
};

const ORIENTATION_LABELS: Record<MediaOrientation, string> = {
  landscape: "Landscape",
  portrait: "Portrait",
  square: "Fallback",
};

type VariantSlot = "default" | "landscape" | "portrait";

const VARIANT_SLOT_ORDER: VariantSlot[] = ["default", "landscape", "portrait"];

const VARIANT_SLOT_LABELS: Record<VariantSlot, string> = {
  default: "Default",
  landscape: "Landscape",
  portrait: "Portrait",
};

const ColorPicker = ({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}) => (
  <div className={`flex gap-2 ${disabled ? "opacity-50" : ""}`}>
    {COLORS.map((color) => (
      <button
        key={color}
        onClick={() => !disabled && onChange(color)}
        disabled={disabled}
        className={`w-6 h-6 rounded-full transition-transform disabled:cursor-not-allowed ${
          value === color ? "ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110" : ""
        }`}
        style={{ backgroundColor: color }}
      />
    ))}
  </div>
);

const getDuration = (item: EditableSlideshowMediaItem, mediaInfo?: MediaFileInfo): number => {
  if (item.durationSeconds) return item.durationSeconds;
  if (mediaInfo?.metadata?.duration) return mediaInfo.metadata.duration;
  return 10;
};

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const getOrientationLabel = (mediaInfo?: MediaFileInfo): string => {
  const orientation = mediaInfo?.metadata?.orientation;
  if (!orientation) return "Default";
  return ORIENTATION_LABELS[orientation];
};

const getVariantSlot = (mediaInfo?: MediaFileInfo): VariantSlot => {
  const orientation = mediaInfo?.metadata?.orientation;
  if (orientation === "landscape") return "landscape";
  if (orientation === "portrait") return "portrait";
  return "default";
};

const renderThumbnail = (mediaInfo?: MediaFileInfo) => {
  if (!mediaInfo) {
    return <div className="animate-pulse bg-zinc-800 w-full h-full" />;
  }

  if (mediaInfo.contentType.startsWith("image/")) {
    return <img src={mediaInfo.downloadUrl} alt="" className="w-full h-full object-cover" />;
  }

  if (mediaInfo.contentType.startsWith("video/")) {
    return <video src={mediaInfo.downloadUrl} className="w-full h-full object-cover" muted preload="metadata" />;
  }

  return <Film size={16} className="text-zinc-600" />;
};

const getImageDimensions = (file: File): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) {
      resolve(null);
      return;
    }

    const image = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(objectUrl);
    };
    image.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(objectUrl);
    };
    image.src = objectUrl;
  });
};

const getVideoMetadata = (file: File): Promise<{ width: number; height: number; duration?: number } | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith("video/")) {
      resolve(null);
      return;
    }

    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: Number.isFinite(video.duration) ? video.duration : undefined,
      });
      URL.revokeObjectURL(objectUrl);
    };
    video.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(objectUrl);
    };
    video.src = objectUrl;
  });
};

const getUploadMetadata = async (file: File): Promise<Record<string, unknown>> => {
  const imageMetadata = await getImageDimensions(file);
  if (imageMetadata) {
    return imageMetadata;
  }

  const videoMetadata = await getVideoMetadata(file);
  if (videoMetadata) {
    return videoMetadata;
  }

  return {};
};

const uploadMediaFile = async (file: File): Promise<MediaFileInfo> => {
  const formData = new FormData();
  const metadata = await getUploadMetadata(file);
  formData.append("file", file);
  if (Object.keys(metadata).length > 0) {
    formData.append("metadata", JSON.stringify(metadata));
  }

  const response = await fetch("/api/media/files", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Upload failed");
  }

  const data = (await response.json()) as UploadResult;
  return data.file;
};

const MediaPreviewDialog = ({
  mediaInfo,
  label,
  onClose,
}: {
  mediaInfo: MediaFileInfo;
  label: string;
  onClose: () => void;
}) => {
  const isVideo = mediaInfo.contentType.startsWith("video/");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="relative max-h-[85vh] max-w-[85vw] overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <div className="min-w-0">
            <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
            <p className="truncate text-sm text-white">{mediaInfo.originalFilename}</p>
          </div>
          <button onClick={onClose} className="ml-4 shrink-0 rounded p-1 hover:bg-zinc-800">
            <X size={18} className="text-zinc-400" />
          </button>
        </div>
        <div className="flex items-center justify-center bg-black p-2">
          {isVideo ? (
            <video
              src={mediaInfo.downloadUrl}
              className="max-h-[70vh] max-w-full object-contain"
              controls
              autoPlay
              muted
            />
          ) : (
            <img
              src={mediaInfo.downloadUrl}
              alt={mediaInfo.originalFilename}
              className="max-h-[70vh] max-w-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const VariantPreviewCell = ({
  label,
  mediaInfo,
  onRemove,
  canEdit,
  onAdd,
  isPortrait,
  onPreview,
}: {
  label: string;
  mediaInfo?: MediaFileInfo;
  onRemove?: () => void;
  canEdit: boolean;
  onAdd?: () => void;
  isPortrait?: boolean;
  onPreview?: () => void;
}) => {
  const thumbWidth = isPortrait ? "w-10" : "w-16";
  const thumbHeight = isPortrait ? "h-14" : "h-10";

  return (
    <button
      type="button"
      onClick={() => {
        if (mediaInfo && onPreview) {
          onPreview();
          return;
        }
        if (!mediaInfo && canEdit && onAdd) onAdd();
      }}
      className={`group/variant flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors ${
        mediaInfo
          ? "border-zinc-700/50 bg-zinc-800/50 hover:border-zinc-600"
          : "border-dashed border-zinc-700 bg-zinc-900/50 hover:border-zinc-500"
      } ${mediaInfo ? "cursor-pointer" : !canEdit ? "cursor-default" : "cursor-pointer"}`}
    >
      <div className={`${thumbWidth} ${thumbHeight} shrink-0 overflow-hidden rounded bg-zinc-900`}>
        {mediaInfo ? (
          renderThumbnail(mediaInfo)
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Plus size={12} className="text-zinc-600" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
        {mediaInfo && <p className="truncate text-xs text-zinc-400">{mediaInfo.originalFilename}</p>}
      </div>
      {mediaInfo && canEdit && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="shrink-0 p-0.5 text-zinc-600 opacity-0 transition-opacity group-hover/variant:opacity-100 hover:text-red-400"
        >
          <Trash2 size={12} />
        </button>
      )}
    </button>
  );
};

const SlideshowItemRow = ({
  item,
  index,
  mediaInfoMap,
  onRemove,
  onDurationChange,
  onMoveUp,
  onMoveDown,
  onRemoveVariant,
  onAddVariant,
  onDropFiles,
  isFirst,
  isLast,
  canEdit,
}: {
  item: EditableSlideshowMediaItem;
  index: number;
  mediaInfoMap: Map<string, MediaFileInfo>;
  onRemove: () => void;
  onDurationChange: (duration: number | undefined) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemoveVariant: (variantIndex: number) => void;
  onAddVariant: () => void;
  onDropFiles: (files: FileList | null) => void;
  isFirst: boolean;
  isLast: boolean;
  canEdit: boolean;
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<{ mediaInfo: MediaFileInfo; label: string } | null>(null);
  const variantsBySlot = item.variants.reduce<Partial<Record<VariantSlot, { mediaId: string; variantIndex: number }>>>(
    (slots, variant, variantIndex) => {
      const mediaInfo = mediaInfoMap.get(variant.mediaId);
      slots[getVariantSlot(mediaInfo)] = { mediaId: variant.mediaId, variantIndex };
      return slots;
    },
    {},
  );

  return (
    <div
      onDragOver={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(true);
      }}
      onDragLeave={(event) => {
        event.stopPropagation();
        setIsDraggingOver(false);
      }}
      onDrop={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);
        onDropFiles(event.dataTransfer.files);
      }}
      className={`group rounded-lg border px-3 py-2 transition-colors ${
        isDraggingOver ? "border-blue-500 bg-blue-500/10" : "border-zinc-700/50 bg-zinc-800/50"
      }`}
    >
      <div className="flex items-center gap-3">
        {canEdit && (
          <div className="flex flex-col gap-0.5">
            <button
              onClick={onMoveUp}
              disabled={isFirst}
              className="p-0.5 text-zinc-500 hover:text-white disabled:opacity-30"
            >
              <ChevronUp size={14} />
            </button>
            <button
              onClick={onMoveDown}
              disabled={isLast}
              className="p-0.5 text-zinc-500 hover:text-white disabled:opacity-30"
            >
              <ChevronDown size={14} />
            </button>
          </div>
        )}

        <span className="w-6 shrink-0 text-sm text-zinc-500">#{index + 1}</span>

        <div className="flex items-center gap-2">
          <Clock size={14} className="text-zinc-500" />
          <input
            type="number"
            min={1}
            max={3600}
            value={item.durationSeconds ?? ""}
            onChange={(event) => onDurationChange(event.target.value ? parseInt(event.target.value, 10) : undefined)}
            placeholder="10"
            disabled={!canEdit}
            className="w-14 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-center text-sm text-white focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
          <span className="text-xs text-zinc-500">sec</span>
        </div>

        <div className="grid min-w-0 flex-1 grid-cols-3 gap-2">
          {VARIANT_SLOT_ORDER.map((slot) => {
            const entry = variantsBySlot[slot];
            const entryMedia = entry ? mediaInfoMap.get(entry.mediaId) : undefined;
            return (
              <VariantPreviewCell
                key={slot}
                label={VARIANT_SLOT_LABELS[slot]}
                mediaInfo={entryMedia}
                onRemove={entry ? () => onRemoveVariant(entry.variantIndex) : undefined}
                onAdd={() => {
                  if (canEdit) onAddVariant();
                }}
                canEdit={canEdit}
                isPortrait={slot === "portrait"}
                onPreview={
                  entryMedia
                    ? () => setPreviewMedia({ mediaInfo: entryMedia, label: VARIANT_SLOT_LABELS[slot] })
                    : undefined
                }
              />
            );
          })}
        </div>

        {canEdit && (
          <button
            onClick={onRemove}
            className="shrink-0 p-1 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {previewMedia && (
        <MediaPreviewDialog
          mediaInfo={previewMedia.mediaInfo}
          label={previewMedia.label}
          onClose={() => setPreviewMedia(null)}
        />
      )}
    </div>
  );
};

const SlideshowEditor = ({
  slideshow,
  onSave,
  onCancel,
  isNew = false,
  canEdit = true,
}: {
  slideshow: Slideshow | null;
  onSave: (data: {
    name: string;
    color: string;
    items: EditableSlideshowMediaItem[];
    groupId: string;
  }) => Promise<void>;
  onCancel: () => void;
  isNew?: boolean;
  canEdit?: boolean;
}) => {
  const apiClient = useApiClient();
  const { groups } = useSlideshows();
  const { currentTenantId } = useTenant();
  const [name, setName] = useState(slideshow?.name ?? "");
  const [color, setColor] = useState(slideshow?.color ?? "#3b82f6");
  const [groupId, setGroupId] = useState<string>(slideshow?.groupId ?? currentTenantId ?? "");
  const [items, setItems] = useState<EditableSlideshowMediaItem[]>(slideshow?.items ?? []);
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [targetRowIndex, setTargetRowIndex] = useState<number | null>(null);
  const [mediaInfoMap, setMediaInfoMap] = useState<Map<string, MediaFileInfo>>(new Map());
  const [isDraggingOverEditor, setIsDraggingOverEditor] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slideshow) return;
    const fetchMediaInfo = async () => {
      try {
        const data = await apiClient.get<{ media: MediaFileInfo[] }>(`/api/slideshows/${slideshow.id}/media`);
        setMediaInfoMap((prev) => {
          const next = new Map(prev);
          for (const file of data.media) next.set(file.id, file);
          return next;
        });
      } catch (fetchError) {
        console.error("Failed to fetch slideshow media:", fetchError);
      }
    };
    void fetchMediaInfo();
  }, [slideshow?.id, apiClient]);

  const addMediaToRow = (file: MediaFileInfo, rowIndex: number | null) => {
    const nextMap = new Map(mediaInfoMap);
    nextMap.set(file.id, file);
    setMediaInfoMap(nextMap);

    if (rowIndex === null) {
      setItems((current) => [
        ...current,
        {
          variants: [{ mediaId: file.id }],
          durationSeconds: file.contentType.startsWith("video/") ? undefined : 10,
        },
      ]);
      return;
    }

    const orientation = file.metadata?.orientation;
    const existingItem = items[rowIndex];
    if (!existingItem) return;

    const hasConflict = existingItem.variants.some((variant) => {
      const existingOrientation = mediaInfoMap.get(variant.mediaId)?.metadata?.orientation;
      return existingOrientation === orientation;
    });

    if (hasConflict) {
      throw new Error(`This row already has a ${getOrientationLabel(file)} variant`);
    }

    setItems((current) =>
      current.map((item, index) =>
        index === rowIndex ? { ...item, variants: [...item.variants, { mediaId: file.id }] } : item,
      ),
    );
  };

  const handleSelectMedia = (file: MediaFileInfo) => {
    try {
      addMediaToRow(file, targetRowIndex);
      setShowMediaPicker(false);
      setTargetRowIndex(null);
      setError(null);
    } catch (selectionError) {
      setError(selectionError instanceof Error ? selectionError.message : "Failed to add media");
    }
  };

  const handleFilesDropped = async (files: FileList | null, rowIndex: number | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        continue;
      }

      try {
        const uploadedFile = await uploadMediaFile(file);
        addMediaToRow(uploadedFile, rowIndex);
      } catch (dropError) {
        setError(dropError instanceof Error ? dropError.message : `Failed to upload ${file.name}`);
        break;
      }
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), color, items, groupId });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Failed to save slideshow");
    } finally {
      setSaving(false);
    }
  };

  const totalRuntime = items.reduce((sum, item) => {
    const primaryMediaId = item.variants[0]?.mediaId;
    return sum + getDuration(item, primaryMediaId ? mediaInfoMap.get(primaryMediaId) : undefined);
  }, 0);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {isNew ? "Create Slideshow" : canEdit ? "Edit Slideshow" : "View Slideshow"}
          </h2>
          <button onClick={onCancel} className="rounded p-1 hover:bg-zinc-800">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>
        {!isNew && slideshow && (
          <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500">
            <span>Created {formatDate(slideshow.createdAt)}</span>
            <span className="text-zinc-700">•</span>
            <span>Modified {formatDate(slideshow.updatedAt)}</span>
            {slideshow.createdBy && (
              <>
                <span className="text-zinc-700">•</span>
                <span>By {slideshow.createdBy}</span>
              </>
            )}
          </div>
        )}
      </div>

      {!canEdit && (
        <div
          className="mx-4 mt-4 rounded p-3 text-sm"
          style={{
            backgroundColor: `${BANNER_COLORS.orange}15`,
            color: BANNER_COLORS.orange,
            border: `1px solid ${BANNER_COLORS.orange}33`,
          }}
        >
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 shrink-0" />
            <span>This slideshow belongs to a higher-level group and cannot be edited from here.</span>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
        )}

        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-400">Name</label>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Slideshow name"
            disabled={!canEdit}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-400">Color</label>
          <ColorPicker value={color} onChange={setColor} disabled={!canEdit} />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-400">Applies To</label>
          <HierarchyPicker
            groups={groups}
            selectedId={groupId || null}
            onChange={(nextId) => nextId && setGroupId(nextId)}
            placeholder="Select target..."
            disabled={!canEdit}
          />
          <p className="mt-1 text-xs text-zinc-500">Choose which part of the organization this slideshow belongs to</p>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium text-zinc-400">Media Items</label>
            {canEdit && (
              <button
                onClick={() => {
                  setTargetRowIndex(null);
                  setShowMediaPicker(true);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-blue-400 hover:text-blue-300"
              >
                <Plus size={14} />
                Add Media
              </button>
            )}
          </div>

          <div
            onDragOver={(event) => {
              if (!canEdit) return;
              event.preventDefault();
              setIsDraggingOverEditor(true);
            }}
            onDragLeave={() => setIsDraggingOverEditor(false)}
            onDrop={(event) => {
              if (!canEdit) return;
              event.preventDefault();
              setIsDraggingOverEditor(false);
              void handleFilesDropped(event.dataTransfer.files, null);
            }}
            className={`rounded-lg border-2 border-dashed p-4 transition-colors ${
              isDraggingOverEditor ? "border-blue-500 bg-blue-500/10" : "border-zinc-700"
            }`}
          >
            {items.length === 0 ? (
              <div className="py-8 text-center">
                <Upload size={32} className="mx-auto mb-2 text-zinc-600" />
                <p className="text-sm text-zinc-500">Drop media here to create your first row</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item, index) => (
                  <SlideshowItemRow
                    key={`${index}-${item.variants.map((variant) => variant.mediaId).join("-")}`}
                    item={item}
                    index={index}
                    mediaInfoMap={mediaInfoMap}
                    onRemove={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    onDurationChange={(duration) =>
                      setItems((current) =>
                        current.map((currentItem, itemIndex) =>
                          itemIndex === index ? { ...currentItem, durationSeconds: duration } : currentItem,
                        ),
                      )
                    }
                    onMoveUp={() => {
                      if (index === 0) return;
                      setItems((current) => {
                        const next = [...current];
                        const previousItem = next[index - 1];
                        next[index - 1] = next[index]!;
                        next[index] = previousItem!;
                        return next;
                      });
                    }}
                    onMoveDown={() => {
                      if (index === items.length - 1) return;
                      setItems((current) => {
                        const next = [...current];
                        const nextItem = next[index + 1];
                        next[index + 1] = next[index]!;
                        next[index] = nextItem!;
                        return next;
                      });
                    }}
                    onRemoveVariant={(variantIndex) => {
                      setItems((current) =>
                        current
                          .map((currentItem, itemIndex) =>
                            itemIndex === index
                              ? {
                                  ...currentItem,
                                  variants: currentItem.variants.filter(
                                    (_, currentVariantIndex) => currentVariantIndex !== variantIndex,
                                  ),
                                }
                              : currentItem,
                          )
                          .filter((currentItem) => currentItem.variants.length > 0),
                      );
                    }}
                    onAddVariant={() => {
                      setTargetRowIndex(index);
                      setShowMediaPicker(true);
                    }}
                    onDropFiles={(files) => {
                      void handleFilesDropped(files, index);
                    }}
                    isFirst={index === 0}
                    isLast={index === items.length - 1}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800 bg-zinc-900/50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-zinc-400">
            <Play size={16} />
            <span className="text-sm">
              Total runtime: <span className="font-medium text-white">{formatDuration(totalRuntime)}</span>
              {items.length > 0 && <span className="ml-2 text-zinc-500">({items.length} items)</span>}
            </span>
          </div>
          <div className="flex gap-3">
            <button onClick={onCancel} className="px-4 py-2 text-zinc-400 hover:text-white">
              {canEdit ? "Cancel" : "Close"}
            </button>
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={!name.trim() || saving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:bg-zinc-700 disabled:text-zinc-500"
              >
                {saving ? "Saving..." : isNew ? "Create" : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>

      {showMediaPicker && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => {
            setShowMediaPicker(false);
            setTargetRowIndex(null);
          }}
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <MediaProvider>
              <MediaBrowser
                title="Select Media"
                pickMode="file"
                allowMultiple={false}
                highlightIds={items.flatMap((item) => item.variants.map((variant) => variant.mediaId))}
                onClose={() => {
                  setShowMediaPicker(false);
                  setTargetRowIndex(null);
                }}
                onPickSingle={(item) => {
                  const file = item as MediaProviderFile;
                  handleSelectMedia({
                    id: file.id,
                    originalFilename: file.originalFilename,
                    contentType: file.contentType,
                    downloadUrl: file.downloadUrl,
                    sizeBytes: file.sizeBytes,
                    metadata: file.metadata as MediaFileInfo["metadata"],
                  });
                }}
              />
            </MediaProvider>
          </div>
        </div>
      )}
    </div>
  );
};

type SlideshowRowView = {
  id: string;
  name: string;
  color: string;
  itemCount: number;
  totalRuntime: number;
  isActive: boolean;
  isArchived: boolean;
  updatedAt: string;
};

const toRowView = (slideshow: Slideshow): SlideshowRowView => {
  const totalRuntime = slideshow.items.reduce((sum, item) => sum + (item.durationSeconds ?? 10), 0);
  return {
    id: slideshow.id,
    name: slideshow.name,
    color: slideshow.color,
    itemCount: slideshow.items.length,
    totalRuntime,
    isActive: false,
    isArchived: false,
    updatedAt: slideshow.updatedAt,
  };
};

const formatRelativeDate = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const SlideshowRow = ({
  slideshow,
  onSelect,
  onDelete,
  canDelete,
}: {
  slideshow: SlideshowRowView;
  onSelect: () => void;
  onDelete: () => void;
  canDelete: boolean;
}) => {
  return (
    <div
      onClick={onSelect}
      className="group flex cursor-pointer items-center gap-5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-5 py-3.5 transition-colors hover:border-zinc-700 hover:bg-zinc-800/60"
    >
      <div className="h-8 w-2 shrink-0 rounded-full" style={{ backgroundColor: slideshow.color }} />
      <p className="min-w-0 flex-1 truncate text-base font-medium text-white">{slideshow.name}</p>
      <div className="hidden shrink-0 items-center gap-8 text-sm text-zinc-500 md:flex">
        <span className="flex min-w-[100px] items-center gap-1.5 tabular-nums">
          <Layers size={14} />
          {slideshow.itemCount} {slideshow.itemCount === 1 ? "item" : "items"}
        </span>
        <span className="flex min-w-[100px] items-center gap-1.5 tabular-nums">
          <Clock size={14} />
          {formatDuration(slideshow.totalRuntime)}
        </span>
        <span className="min-w-[200px] tabular-nums">Updated {formatRelativeDate(slideshow.updatedAt)}</span>
      </div>
      <div className="shrink-0" title={slideshow.isActive ? "Active" : "Inactive"}>
        {slideshow.isActive ? (
          <span className="inline-flex items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 p-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center rounded-full border border-zinc-700 bg-zinc-800/50 p-2">
            <span className="h-2 w-2 rounded-full bg-zinc-600" />
          </span>
        )}
      </div>
      {canDelete && (
        <button
          onClick={(event) => {
            event.stopPropagation();
            if (confirm(`Delete "${slideshow.name}"?`)) onDelete();
          }}
          className="shrink-0 p-2 text-zinc-600 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>
  );
};

type StatusFilter = "current" | "active" | "inactive" | "archived";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "current", label: "Current" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "archived", label: "Archived" },
];

const SlideshowList = ({
  slideshows,
  onSelect,
  onDelete,
}: {
  slideshows: Slideshow[];
  onSelect: (slideshow: Slideshow) => void;
  onDelete: (slideshow: Slideshow) => void;
}) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("current");
  const [colorFilter, setColorFilter] = useState<Set<string>>(new Set());

  const toggleColor = (color: string) => {
    setColorFilter((current) => {
      const next = new Set(current);
      if (next.has(color)) next.delete(color);
      else next.add(color);
      return next;
    });
  };

  const slideshowsById = new Map(slideshows.map((slideshow) => [slideshow.id, slideshow]));

  const filtered = slideshows.map(toRowView).filter((slideshow) => {
    if (statusFilter === "current" && slideshow.isArchived) return false;
    if (statusFilter === "active" && (!slideshow.isActive || slideshow.isArchived)) return false;
    if (statusFilter === "inactive" && (slideshow.isActive || slideshow.isArchived)) return false;
    if (statusFilter === "archived" && !slideshow.isArchived) return false;
    if (colorFilter.size > 0 && !colorFilter.has(slideshow.color)) return false;
    if (search.trim() && !slideshow.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <div className="mx-auto w-full max-w-[84rem] space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative h-9 min-w-[220px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search slideshows..."
            className="h-full w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-9 pr-3 text-sm text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div className="flex h-9 items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5">
          {COLORS.map((color) => {
            const isSelected = colorFilter.has(color);
            return (
              <button
                key={color}
                type="button"
                onClick={() => toggleColor(color)}
                title={isSelected ? "Remove filter" : "Filter by color"}
                className={`h-4 w-4 rounded-full transition-transform hover:scale-110 ${
                  isSelected ? "ring-2 ring-white ring-offset-1 ring-offset-zinc-900" : ""
                }`}
                style={{ backgroundColor: color }}
              />
            );
          })}
        </div>
        <div className="flex h-9 items-center rounded-lg border border-zinc-800 bg-zinc-900 p-0.5 text-xs">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setStatusFilter(option.key)}
              className={`rounded-md px-3 py-1.5 transition-colors ${
                statusFilter === option.key ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-zinc-500">
          {filtered.length} {filtered.length === 1 ? "slideshow" : "slideshows"}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 py-12 text-center text-sm text-zinc-500">
          No slideshows match this filter
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((view) => {
            const source = slideshowsById.get(view.id)!;
            return (
              <SlideshowRow
                key={view.id}
                slideshow={view}
                onSelect={() => onSelect(source)}
                onDelete={() => onDelete(source)}
                canDelete={source.canEdit}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const ImportIndySlideshowsDialog = ({
  isOpen,
  groups,
  defaultGroupId,
  onClose,
  onImported,
}: {
  isOpen: boolean;
  groups: ReturnType<typeof useSlideshows>["groups"];
  defaultGroupId: string | null;
  onClose: () => void;
  onImported: () => Promise<void>;
}) => {
  const apiClient = useApiClient();
  const [slideshows, setSlideshows] = useState<IndyExternalSlideshow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [groupId, setGroupId] = useState(defaultGroupId ?? "");
  const [destinationFolder, setDestinationFolder] = useState<MediaProviderFolder | null>(null);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedImports, setFailedImports] = useState<IndyImportResponse["failed"]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const loadSlideshows = async () => {
      setLoading(true);
      setError(null);
      setFailedImports([]);
      setSelectedIds(new Set());
      setGroupId(defaultGroupId ?? "");
      setDestinationFolder(null);

      try {
        const data = await apiClient.get<IndySlideshowsResponse>("/api/integrations/indy/slideshows");
        setSlideshows(data.slideshows);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Failed to load Indy Media slideshows");
      } finally {
        setLoading(false);
      }
    };

    void loadSlideshows();
  }, [isOpen, apiClient, defaultGroupId]);

  const toggleSlideshow = (id: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setFailedImports([]);

    try {
      const response = await apiClient.post<IndyImportResponse>("/api/integrations/indy/slideshows/import", {
        ids: [...selectedIds],
        groupId,
        parentFolderId: destinationFolder?.id,
      });
      await onImported();

      if (response.failed.length > 0) {
        setFailedImports(response.failed);
        return;
      }

      onClose();
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : "Failed to import slideshows");
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Import Indy Media Slideshows</h2>
            <p className="mt-1 text-sm text-zinc-500">Choose the slideshows to clone into IndyTV.</p>
          </div>
          <button onClick={onClose} disabled={importing} className="rounded p-1 hover:bg-zinc-800 disabled:opacity-50">
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {failedImports.length > 0 && (
            <div className="mb-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-300">
              <p className="font-medium">Some slideshows could not be imported.</p>
              <div className="mt-2 space-y-1">
                {failedImports.map((failure) => (
                  <p key={failure.externalId}>
                    {failure.name}: {failure.error}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="mb-5">
            <label className="mb-2 block text-sm font-medium text-zinc-400">Slideshow Group</label>
            <HierarchyPicker
              groups={groups}
              selectedId={groupId || null}
              onChange={(nextId) => nextId && setGroupId(nextId)}
              placeholder="Select target..."
              disabled={importing}
            />
            <p className="mt-1 text-xs text-zinc-500">Which part of the organisation owns these slideshows.</p>
          </div>

          <div className="mb-5">
            <label className="mb-2 block text-sm font-medium text-zinc-400">Destination Folder</label>
            <button
              type="button"
              onClick={() => setShowFolderPicker(true)}
              disabled={importing}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-left hover:border-zinc-600 disabled:opacity-50"
            >
              <span className="flex min-w-0 items-center gap-2">
                <FolderIcon size={16} className="shrink-0 text-blue-400" />
                <span className={`truncate text-sm ${destinationFolder ? "text-white" : "text-zinc-500"}`}>
                  {destinationFolder ? destinationFolder.name : "Choose a folder..."}
                </span>
              </span>
              <span className="shrink-0 text-xs text-zinc-500">{destinationFolder ? "Change" : "Pick"}</span>
            </button>
            <p className="mt-1 text-xs text-zinc-500">A subfolder will be created here for each imported slideshow.</p>
          </div>

          {loading ? (
            <div className="flex h-40 items-center justify-center text-zinc-500">Loading Indy Media slideshows...</div>
          ) : slideshows.length === 0 ? (
            <div className="rounded-lg border border-zinc-800 p-8 text-center text-zinc-500">
              No Indy Media slideshows found.
            </div>
          ) : (
            <div className="space-y-2">
              {slideshows.map((slideshow) => (
                <label
                  key={slideshow.id}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 transition-colors hover:border-zinc-700"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(slideshow.id)}
                    onChange={() => toggleSlideshow(slideshow.id)}
                    disabled={importing}
                    className="mt-1 h-4 w-4 rounded border-zinc-600 bg-zinc-800"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-white">{slideshow.name}</p>
                      <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-400">
                        {slideshow.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      {slideshow.attachmentCount} {slideshow.attachmentCount === 1 ? "media item" : "media items"} •{" "}
                      {slideshow.interval || 10}s interval
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-zinc-800 px-5 py-4">
          <p className="text-sm text-zinc-500">
            {selectedIds.size} {selectedIds.size === 1 ? "slideshow" : "slideshows"} selected
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} disabled={importing} className="px-4 py-2 text-zinc-400 hover:text-white">
              Close
            </button>
            <button
              onClick={handleImport}
              disabled={selectedIds.size === 0 || !groupId || !destinationFolder || importing || loading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500"
            >
              {importing ? "Importing..." : "Import Selected"}
            </button>
          </div>
        </div>
      </div>

      {showFolderPicker && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70"
          onClick={() => setShowFolderPicker(false)}
        >
          <div
            className="flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900"
            onClick={(e) => e.stopPropagation()}
          >
            <MediaProvider>
              <MediaBrowser
                title="Select destination folder"
                pickMode="folder"
                allowMultiple={false}
                showFiles={false}
                onClose={() => setShowFolderPicker(false)}
                onPickSingle={(item) => {
                  setDestinationFolder(item as MediaProviderFolder);
                  setShowFolderPicker(false);
                }}
              />
            </MediaProvider>
          </div>
        </div>
      )}
    </div>
  );
};

const SlideshowsContent = () => {
  const { slideshows, groups, loading, error, fetchSlideshows, createSlideshow, updateSlideshow, deleteSlideshow } =
    useSlideshows();
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [editingSlideshow, setEditingSlideshow] = useState<Slideshow | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [indyConnection, setIndyConnection] = useState<IndyConnection | null>(null);

  useEffect(() => {
    const loadConnection = async () => {
      try {
        const data = await apiClient.get<IndyConnectionResponse>("/api/integrations/indy/connection");
        setIndyConnection(data.connection);
      } catch {
        setIndyConnection({ connected: false });
      }
    };

    void loadConnection();
  }, [apiClient]);

  const canImportFromIndy = indyConnection?.connected === true;

  if (isCreating) {
    return (
      <SlideshowEditor
        slideshow={null}
        onSave={async (data) => {
          await createSlideshow(data);
          setIsCreating(false);
        }}
        onCancel={() => setIsCreating(false)}
        isNew
      />
    );
  }

  if (editingSlideshow) {
    return (
      <SlideshowEditor
        slideshow={editingSlideshow}
        onSave={async (data) => {
          await updateSlideshow(editingSlideshow.id, data);
          setEditingSlideshow(null);
        }}
        onCancel={() => setEditingSlideshow(null)}
        canEdit={editingSlideshow.canEdit}
      />
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Slideshows</h1>
          <p className="mt-1 text-sm text-zinc-400">Create and manage media slideshows</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setImportDialogOpen(true)}
            disabled={!canImportFromIndy}
            title={canImportFromIndy ? "Import slideshows from Indy Media" : "Connect Indy Media in Settings first"}
            className="flex items-center gap-2 rounded-lg bg-zinc-800 px-4 py-2 text-zinc-200 hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Upload size={16} />
            Import Slideshows
          </button>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-500"
          >
            <Plus size={16} />
            New Slideshow
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-zinc-500">Loading...</div>
        </div>
      ) : (
        <SlideshowList
          slideshows={slideshows}
          onSelect={(slideshow) => setEditingSlideshow(slideshow)}
          onDelete={async (slideshow) => {
            await deleteSlideshow(slideshow.id);
          }}
        />
      )}

      <ImportIndySlideshowsDialog
        isOpen={importDialogOpen}
        groups={groups}
        defaultGroupId={currentTenantId}
        onClose={() => setImportDialogOpen(false)}
        onImported={fetchSlideshows}
      />
    </div>
  );
};

export default () => {
  const { currentTenantId } = useTenant();
  return (
    <SlideshowsProvider key={currentTenantId ?? "no-tenant"}>
      <SlideshowsContent />
    </SlideshowsProvider>
  );
};
